import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { FormError } from "@/components/form/form-error";
export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const { login, loginWithGoogle, user } = useAuth();
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const navigate = useNavigate();
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorText(null);
        try {
            await login(email, password);
            // Check if user has pending booking, redirect back to booking page
            const hasPendingBooking = sessionStorage.getItem('pendingBooking');
            if (hasPendingBooking) {
                navigate('/booking');
            }
            else {
                // Always redirect to /dashboard - it will show different content based on role
                navigate('/dashboard');
            }
        }
        catch (error) {
            console.error("Login failed:", error);
            const msg = error instanceof Error ? error.message : 'Login failed';
            // If email not verified, redirect to verify page
            if (msg.toLowerCase().includes('email not verified')) {
                navigate(`/verify?email=${encodeURIComponent(email)}`);
                return;
            }
            setErrorText(msg);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleGoogleLogin = async () => {
        setIsGoogleLoading(true);
        setErrorText(null);
        try {
            await loginWithGoogle();
            // Check if user has pending booking, redirect back to booking page
            const hasPendingBooking = sessionStorage.getItem('pendingBooking');
            if (hasPendingBooking) {
                navigate('/booking');
            }
            else {
                navigate('/dashboard');
            }
        }
        catch (error) {
            console.error('Google login failed:', error);
            const msg = error instanceof Error ? error.message : 'Google login failed';
            setErrorText(msg);
        }
        finally {
            setIsGoogleLoading(false);
        }
    };
    // Redirect to dashboard if already logged in (it will show role-appropriate view)
    if (user) {
        navigate('/dashboard', { replace: true });
        return null;
    }
    return (_jsx("div", { className: "min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black flex items-center justify-center p-4", children: _jsxs("div", { className: "w-full max-w-md", children: [_jsx("div", { className: "text-center mb-8", children: _jsxs(Link, { to: "/", className: "inline-block", children: [_jsx("h1", { className: "text-3xl font-bold bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent", children: "K one Golf" }), _jsx("p", { className: "text-sm text-slate-400 mt-1", children: "Premium Screen Golf" })] }) }), _jsxs(Card, { className: "shadow-2xl bg-slate-800/50 border-slate-700 backdrop-blur-sm", children: [_jsxs(CardHeader, { className: "space-y-1", children: [_jsx(CardTitle, { className: "text-2xl font-bold text-center text-white", children: "Welcome Back" }), _jsx(CardDescription, { className: "text-center text-slate-400", children: "Sign in to your account to book premium screen golf rooms" })] }), _jsxs(CardContent, { children: [_jsxs("form", { onSubmit: handleSubmit, className: "space-y-4", children: [_jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "email", className: "text-slate-300", children: "Email" }), _jsx(Input, { id: "email", type: "email", placeholder: "Enter your email", value: email, onChange: (e) => { setEmail(e.target.value); if (errorText)
                                                        setErrorText(null); }, required: true, className: "w-full bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-amber-500" })] }), _jsxs("div", { className: "space-y-2", children: [_jsx(Label, { htmlFor: "password", className: "text-slate-300", children: "Password" }), _jsx(Input, { id: "password", type: "password", placeholder: "Enter your password", value: password, onChange: (e) => { setPassword(e.target.value); if (errorText)
                                                        setErrorText(null); }, required: true, className: "w-full bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 focus:border-amber-500" })] }), _jsx("div", { className: "flex justify-end", children: _jsx(Link, { to: "/forgot-password", className: "text-sm text-amber-400 hover:text-amber-300", children: "Forgot password?" }) }), _jsx(FormError, { message: errorText }), _jsx(Button, { type: "submit", className: "w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold", disabled: isLoading, children: isLoading ? "Signing In..." : "Sign In" })] }), _jsx("div", { className: "mt-6 text-center", children: _jsxs("p", { className: "text-sm text-slate-400", children: ["Don't have an account?", " ", _jsx(Link, { to: "/signup", className: "text-amber-400 hover:text-amber-300 font-medium", children: "Sign up" })] }) })] })] })] }) }));
}
